import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Flame, Car, Swords, Cable, Film, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';

const ServicesPage = () => {
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    project_type: '',
    description: '',
    budget_range: '',
    start_date: '',
    duration: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const services = [
    {
      icon: Swords,
      title: 'Cascades Physiques',
      description: 'Combats réalistes, réactions aux impacts, chutes et bagarres chorégraphiées pour le cinéma.',
      features: ['Combats à mains nues', 'Arts martiaux', 'Escrime de scène', 'Bagarres de groupe'],
    },
    {
      icon: Car,
      title: 'Mécaniques Auto-Moto-Bateau',
      description: 'Poursuites, crash, travelling action sur tous types de véhicules motorisés.',
      features: ['Poursuites automobiles', 'Cascades moto', 'Action nautique', 'Travelling véhicule'],
    },
    {
      icon: Flame,
      title: 'Torche & Pyrotechnie',
      description: 'Torches humaines, body burns, explosions contrôlées et effets pyrotechniques.',
      features: ['Torche complète', 'Body burns partiels', 'Explosions', 'Impacts squibs'],
    },
    {
      icon: Cable,
      title: 'Chutes de Hauteur & Câblage',
      description: 'Chutes contrôlées, vols sur câbles, projections et suspensions aériennes.',
      features: ['Chutes 2 à 15m', 'Vols aériens', 'Projections', 'Ratchets'],
    },
    {
      icon: Film,
      title: 'Réalisation 2ème Équipe Action',
      description: 'Direction et réalisation des séquences d\'action en 2ème équipe, du storyboard au tournage.',
      features: ['Storyboard action', 'Préviz', 'Réalisation 2nd unit', 'Coordination plateau'],
    },
    {
      icon: Users,
      title: 'Régleur & Coordination',
      description: 'Coordination complète des cascades, casting de cascadeurs et doublures.',
      features: ['Réglage cascade', 'Casting cascadeurs', 'Doublures', 'Sécurité plateau'],
    },
  ];

  const projectTypes = [
    'Long métrage',
    'Série TV',
    'Court métrage',
    'Publicité',
    'Clip musical',
    'Spectacle vivant',
    'Événementiel',
    'Autre',
  ];

  const budgetRanges = [
    'Moins de 5 000€',
    '5 000€ - 15 000€',
    '15 000€ - 50 000€',
    '50 000€ - 100 000€',
    'Plus de 100 000€',
    'À définir',
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/quotes`, formData);
      setSubmitted(true);
      toast.success('Demande de devis envoyée avec succès !');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black pt-20">
      {/* Header */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <p className="font-['Barlow'] text-[#FACC15] uppercase tracking-[0.3em] text-sm mb-4">
              Pour les professionnels
            </p>
            <h1 
              className="font-['Teko'] text-6xl md:text-8xl text-white mb-6"
              data-testid="services-title"
            >
              NOS SERVICES
            </h1>
            <p className="font-['Barlow'] text-gray-400 text-lg max-w-2xl mx-auto">
              Cascades physiques, mécaniques auto-moto-bateau, crash, torche, chute de hauteur, câblage.
              Réalisation en 2ème équipe action. Une équipe complète pour vos séquences d'action.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="pb-24">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="group bg-[#09090b] border border-white/10 p-8 hover:border-[#FACC15]/30 transition-colors cursor-pointer"
                data-testid={`service-${index}`}
                onClick={() => {
                  const formSection = document.getElementById('quote-form');
                  if (formSection) {
                    formSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              >
                <service.icon className="w-12 h-12 text-[#FACC15] mb-6" />
                <h3 className="font-['Teko'] text-2xl text-white uppercase mb-3">
                  {service.title}
                </h3>
                <p className="font-['Barlow'] text-gray-400 mb-6 leading-relaxed">
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-gray-300 font-['Barlow'] text-sm">
                      <Check size={14} className="text-[#FACC15]" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-4 border-t border-white/10">
                  <span className="font-['Barlow'] text-[#FACC15] text-sm uppercase tracking-wider group-hover:underline">
                    Demander un devis →
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Form */}
      <section id="quote-form" className="py-24 bg-[#09090b] border-t border-white/10">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <p className="font-['Barlow'] text-[#FACC15] uppercase tracking-[0.3em] text-sm mb-4">
              Demande de devis
            </p>
            <h2 className="font-['Teko'] text-5xl md:text-6xl text-white">
              PARLONS DE VOTRE PROJET
            </h2>
          </motion.div>

          {submitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-black border border-[#FACC15]/30 p-12 text-center"
            >
              <div className="w-16 h-16 bg-[#FACC15] mx-auto mb-6 flex items-center justify-center">
                <Check size={32} className="text-black" />
              </div>
              <h3 className="font-['Teko'] text-3xl text-white mb-4">
                DEMANDE ENVOYÉE !
              </h3>
              <p className="font-['Barlow'] text-gray-400">
                Nous avons bien reçu votre demande de devis. 
                Notre équipe vous contactera dans les plus brefs délais.
              </p>
            </motion.div>
          ) : (
            <motion.form
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              onSubmit={handleSubmit}
              className="bg-black border border-white/10 p-8 md:p-12 space-y-8"
              data-testid="quote-form"
            >
              {/* Contact Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">
                    Société / Production
                  </Label>
                  <Input
                    type="text"
                    required
                    value={formData.company_name}
                    onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                    className="bg-transparent border-white/20 focus:border-[#FACC15] text-white rounded-none"
                    placeholder="Nom de votre société"
                    data-testid="input-company"
                  />
                </div>
                <div>
                  <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">
                    Nom du contact
                  </Label>
                  <Input
                    type="text"
                    required
                    value={formData.contact_name}
                    onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                    className="bg-transparent border-white/20 focus:border-[#FACC15] text-white rounded-none"
                    placeholder="Votre nom"
                    data-testid="input-contact"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">
                    Email
                  </Label>
                  <Input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="bg-transparent border-white/20 focus:border-[#FACC15] text-white rounded-none"
                    placeholder="votre@email.com"
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">
                    Téléphone
                  </Label>
                  <Input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="bg-transparent border-white/20 focus:border-[#FACC15] text-white rounded-none"
                    placeholder="06 XX XX XX XX"
                    data-testid="input-phone"
                  />
                </div>
              </div>

              {/* Project Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">
                    Type de projet
                  </Label>
                  <Select 
                    value={formData.project_type} 
                    onValueChange={(value) => setFormData({...formData, project_type: value})}
                  >
                    <SelectTrigger 
                      className="bg-transparent border-white/20 focus:border-[#FACC15] text-white rounded-none"
                      data-testid="select-project-type"
                    >
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#09090b] border-white/20">
                      {projectTypes.map((type) => (
                        <SelectItem key={type} value={type} className="text-white hover:bg-[#18181b]">
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">
                    Budget estimé
                  </Label>
                  <Select 
                    value={formData.budget_range} 
                    onValueChange={(value) => setFormData({...formData, budget_range: value})}
                  >
                    <SelectTrigger 
                      className="bg-transparent border-white/20 focus:border-[#FACC15] text-white rounded-none"
                      data-testid="select-budget"
                    >
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#09090b] border-white/20">
                      {budgetRanges.map((range) => (
                        <SelectItem key={range} value={range} className="text-white hover:bg-[#18181b]">
                          {range}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">
                    Date de début souhaitée
                  </Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    className="bg-transparent border-white/20 focus:border-[#FACC15] text-white rounded-none"
                    data-testid="input-date"
                  />
                </div>
                <div>
                  <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">
                    Durée estimée du tournage
                  </Label>
                  <Input
                    type="text"
                    value={formData.duration}
                    onChange={(e) => setFormData({...formData, duration: e.target.value})}
                    className="bg-transparent border-white/20 focus:border-[#FACC15] text-white rounded-none"
                    placeholder="Ex: 3 jours, 2 semaines..."
                    data-testid="input-duration"
                  />
                </div>
              </div>

              <div>
                <Label className="text-[#FACC15] text-sm uppercase tracking-wider mb-2 block">
                  Description du projet
                </Label>
                <Textarea
                  required
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="bg-transparent border-white/20 focus:border-[#FACC15] text-white rounded-none min-h-[150px]"
                  placeholder="Décrivez votre projet et les séquences d'action envisagées..."
                  data-testid="input-description"
                />
              </div>

              <div className="pt-6">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#FACC15] text-black font-bold uppercase tracking-widest py-6 text-lg hover:bg-[#EAB308] transition-colors"
                  data-testid="submit-quote"
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="spinner" />
                      Envoi en cours...
                    </span>
                  ) : (
                    'Envoyer ma demande de devis'
                  )}
                </Button>
              </div>
            </motion.form>
          )}
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
